import {
  Button,
  ButtonGroup,
  HStack,
  Icon,
  Image,
  Spacer,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { MapPageView } from "../../../../project/stores/MapStore";
import { CampaignSelection } from "./CampaignSelection";
import { useStores } from "../../../../shared/stores/RootStore";
import logo from "@keita/lanepatrol-sdk/assets/logo/logo-vertical.png";
import {
  ArrowRightIcon,
  ExportIcon,
  MapIcon,
  StatsIcon,
  ImportIcon,
} from "../../../../shared/views/icons";
import { CsvExportModal } from "./CsvExportModal";
import { Link } from "react-router-dom";
import { appRoutes } from "../../../../../App";
import { CsvImportModal } from "./CsvImportModal";

const HeaderBase: React.FC<{ isHome: boolean }> = ({ isHome }) => {
  const {
    authStore,
    mapStore,
    projectStore,
    osaNodeStore,
    osaWayStore,
    hlaStore,
  } = useStores();
  const exportModal = useDisclosure();
  const importModal = useDisclosure();
  const selectedOsaNode = osaNodeStore.getSelected();
  const selectedOsaWay = osaWayStore.getSelected();
  const selectedProject = projectStore.getSelected();
  const selectedHlaWay = hlaStore.getSelected();
  const nodes = selectedOsaWay
    ? osaNodeStore.nodesByWayId.get(selectedOsaWay.id)
    : undefined;

  const projectIsClickable = !!selectedOsaWay || !!selectedHlaWay;

  return (
    <>
      <HStack gap={4} minHeight={12} px={4} flexGrow={1}>
        <HStack
          gap={4}
          separator={<Icon as={ArrowRightIcon} borderInline={"none"} />}
        >
          <Link to={appRoutes.home()}>
            <Image height="40px" src={logo} alt="Logo" />
          </Link>

          {!!selectedProject && (
            <Text
              fontSize="sm"
              fontWeight="semibold"
              cursor={projectIsClickable ? "pointer" : "unset"}
              onClick={() => {
                if (!projectIsClickable) return;

                osaWayStore.clearSelected();
                hlaStore.clearSelected();
                mapStore.recenterMap();
              }}
            >{`${selectedProject.city}: ${selectedProject.name}`}</Text>
          )}

          {!isHome && <CampaignSelection />}

          {!!selectedOsaWay && (
            <Text
              fontSize="sm"
              fontWeight="semibold"
              cursor={selectedOsaNode ? "pointer" : "unset"}
              onClick={() => {
                if (!selectedOsaNode) return;
                osaNodeStore.clearSelected();
                mapStore.recenterMap();
              }}
            >
              {selectedOsaWay.name}
            </Text>
          )}

          {!!selectedOsaNode && !!nodes && (
            <Text fontSize="sm" fontWeight="semibold">
              {t("map:nodeSummary.title", {
                position: nodes.indexOf(selectedOsaNode) + 1,
                total: nodes.length,
              })}
            </Text>
          )}

          {!!selectedHlaWay && (
            <Text fontSize="sm" fontWeight="semibold">
              {selectedHlaWay.name ?? "Unknown street"}
            </Text>
          )}
        </HStack>

        <Spacer />

        {!isHome && (
          <ButtonGroup
            colorPalette="primary"
            size="sm"
            variant="ghost"
            fontSize={"sm"}
          >
            <Button
              onClick={
                mapStore.view === MapPageView.Dashboard
                  ? () => mapStore.setView(MapPageView.Map)
                  : () => mapStore.setView(MapPageView.Dashboard)
              }
            >
              <Icon
                as={
                  mapStore.view === MapPageView.Dashboard ? MapIcon : StatsIcon
                }
              />
              {mapStore.view === MapPageView.Dashboard
                ? t(`map:header.viewMapBtn`)
                : t(`map:header.viewDashboardBtn`)}
            </Button>

            {selectedProject?.permissions?.canManage && (
              <Button onClick={exportModal.onOpen}>
                <Icon as={ExportIcon} /> {t(`map:header.export`)}
              </Button>
            )}
            {authStore.isSuperAdmin && (
              <Button onClick={importModal.onOpen}>
                <Icon as={ImportIcon} /> Import data
              </Button>
            )}
          </ButtonGroup>
        )}
      </HStack>

      {exportModal.open && <CsvExportModal onClose={exportModal.onClose} />}
      {importModal.open && <CsvImportModal onClose={importModal.onClose} />}
    </>
  );
};

export const Header = observer(HeaderBase);
